import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useCart } from "../context/CartContext";
import useProducts from "../hooks/useProducts";
import Loader from "../components/UI/Loader";
import SEO from "../components/UI/SEO";
import SchemaMarkup from "../components/UI/SchemaMarkup";
import ProductDetailInfo from "../components/Marketplace/ProductDetailInfo";
import ProductGrid from "../components/Marketplace/ProductGrid";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const {
    selectedProduct,
    loadProductBySlug,
    detailLoading,
    error,
    featuredProducts,
  } = useProducts({ page_size: 20 });

  const [quantity, setQuantity] = useState(1);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    loadProductBySlug(slug);
  }, [slug, loadProductBySlug]);

  const relatedProducts = useMemo(() => {
    if (!selectedProduct) return [];
    return featuredProducts
      .filter(
        (item) =>
          item.slug !== selectedProduct.slug &&
          item.category_slug === selectedProduct.category_slug
      )
      .slice(0, 3);
  }, [featuredProducts, selectedProduct]);

  const schema = useMemo(() => {
    if (!selectedProduct) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      name: selectedProduct.name,
      description: selectedProduct.short_description || selectedProduct.description || "",
      sku: selectedProduct.sku || "",
      image: selectedProduct.featured_image ? [selectedProduct.featured_image] : [],
      brand: {
        "@type": "Brand",
        name: "Solusi IoT Cerdas",
      },
      offers: {
        "@type": "Offer",
        priceCurrency: "IDR",
        price: Number(selectedProduct.price || 0),
        availability:
          Number(selectedProduct.stock) > 0
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
        url: `${window.location.origin}/product/${selectedProduct.slug}`,
      },
    };
  }, [selectedProduct]);

  const handleAddToCart = () => {
    if (!selectedProduct) return;
    addToCart(selectedProduct, quantity);
    setFeedback("Produk berhasil ditambahkan ke keranjang.");
    window.setTimeout(() => setFeedback(""), 2500);
  };

  if (detailLoading) {
    return <Loader text="Memuat detail produk..." />;
  }

  if (!selectedProduct) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Produk tidak ditemukan</h1>
        <p className="mt-3 text-gray-600">{error || "Slug produk tidak tersedia."}</p>
        <Link
          to="/marketplace"
          className="mt-6 inline-flex rounded-xl bg-orange-600 px-5 py-3 text-sm font-semibold text-white hover:bg-orange-700"
        >
          Kembali ke marketplace
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${selectedProduct.name} | Solusi IoT Cerdas`}
        description={
          selectedProduct.short_description ||
          selectedProduct.description ||
          "Detail produk Solusi IoT Cerdas"
        }
        image={selectedProduct.featured_image}
        type="product"
        canonicalPath={`${window.location.origin}/product/${selectedProduct.slug}`}
      />
      <SchemaMarkup schema={schema} />

      <section className="bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-orange-600">Beranda</Link>
            <span>/</span>
            <Link to="/marketplace" className="hover:text-orange-600">Marketplace</Link>
            <span>/</span>
            <span className="text-gray-900">{selectedProduct.name}</span>
          </nav>

          <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
              <div className="aspect-[4/3] bg-gray-100">
                {selectedProduct.featured_image ? (
                  <img
                    src={selectedProduct.featured_image}
                    alt={selectedProduct.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-sm text-gray-400">
                    Tidak ada gambar produk
                  </div>
                )}
              </div>

              <div className="border-t border-gray-100 p-6">
                <h2 className="text-lg font-semibold text-gray-900">Deskripsi lengkap</h2>
                <p className="mt-3 whitespace-pre-line text-sm leading-7 text-gray-600">
                  {selectedProduct.description || "Belum ada deskripsi lengkap."}
                </p>
              </div>
            </div>

            <div>
              <ProductDetailInfo
                product={selectedProduct}
                quantity={quantity}
                setQuantity={setQuantity}
                onAddToCart={handleAddToCart}
              />

              {feedback ? (
                <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  {feedback}
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-12">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-600">
                  Rekomendasi
                </p>
                <h2 className="mt-2 text-2xl font-bold text-gray-900">
                  Produk terkait
                </h2>
              </div>
            </div>
            <ProductGrid
              products={relatedProducts}
              onAddToCart={(product) => addToCart(product, 1)}
            />
          </div>
        </div>
      </section>
    </>
  );
}
